/**
 * The camera pipeline module list.
 *
 * The order is load-bearing and is exactly the kind of thing that gets silently
 * reshuffled during a refactor, so it lives in one named function with this
 * comment attached:
 *
 *   GlTextureRenderer  draws the camera feed into the canvas
 *   Threejs            creates the three.js scene/camera/renderer
 *   XrController       SLAM world tracking (requires the `slam` chunk)
 *   FullWindowCanvas   sizes the canvas; must run before anything reads it
 *   RuntimeError       shows an error surface if a later module throws
 *   CanvasScreenshot   captures the canvas for the photo feature
 *   appModule          ours; must be last so the scene already exists
 *
 * `CanvasScreenshot` is the one entry whose position does NOT matter: it only
 * implements `onAttach`/`onDetach`, where it grabs the canvas and allocates a 2D
 * context, and it has no per-frame callback at all - so it costs nothing while
 * nobody is taking a photo. It is still placed before `appModule`, so the rule
 * "appModule is last" survives as something that can be read off the list.
 *
 * Deliberately absent:
 *
 * - `XRExtras.Loading` - its iOS in-app-browser branch falls through to
 *   `displayAndroidLinkOutView()`, rendering an Android `intent://` button on
 *   an iPhone. We ship our own Japanese loading UI instead.
 * - `LandingPage` - English-only copy, unsuitable for a Japanese campaign.
 */
export interface PipelineDeps {
  readonly xr8: Xr8Global
  readonly xrextras: XrExtrasGlobal
  readonly appModule: Xr8PipelineModule
}

export const buildPipelineModules = ({
  xr8,
  xrextras,
  appModule,
}: PipelineDeps): readonly Xr8PipelineModule[] => [
  xr8.GlTextureRenderer.pipelineModule(),
  xr8.Threejs.pipelineModule(),
  xr8.XrController.pipelineModule(),
  xrextras.FullWindowCanvas.pipelineModule(),
  xrextras.RuntimeError.pipelineModule(),
  xrextras.PauseOnHidden.pipelineModule(),
  xr8.CanvasScreenshot.pipelineModule(),
  appModule,
]
